import React, { useState, useEffect } from 'react';
import { Thermometer, ShoppingBag, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../../utils/api';


export default function SuitRecommendation({ waterTempF }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!waterTempF || waterTempF <= 0) return;
    let cancelled = false;
    setLoading(true);
    authFetch(`${API}/api/suits/recommend?water_temp_f=${waterTempF}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setRec(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [waterTempF]);

  if (loading || !rec) return null;

  const hasMatch = rec.has_match;
  const hasMissing = rec.missing_types?.length > 0 && !hasMatch;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border p-3 ${
        hasMatch
          ? 'bg-primary/[0.06] border-primary/20'
          : 'bg-secondary/[0.06] border-secondary/20'
      }`}
      data-testid="suit-recommendation"
    >
      <div className="flex items-start gap-2.5">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
          hasMatch ? 'bg-primary/15' : 'bg-secondary/15'
        }`}>
          {hasMatch ? (
            <span className="text-sm">🤿</span>
          ) : (
            <ShoppingBag className="w-3.5 h-3.5 text-secondary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-bold">Suit Check</p>
            <div className="flex items-center gap-0.5 text-[9px] text-blue-400/60">
              <Thermometer className="w-2.5 h-2.5" />
              {Math.round(waterTempF)}&deg;F water
            </div>
          </div>

          <p className={`text-xs font-semibold ${hasMatch ? 'text-primary' : 'text-secondary'}`}>
            {rec.text}
          </p>

          {/* Show matching suits from closet */}
          {hasMatch && rec.matching_suits?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {rec.matching_suits.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[9px] bg-primary/10 text-primary/80 rounded-md px-1.5 py-0.5">
                  <Check className="w-2 h-2" />
                  {s.brand ? `${s.brand} ` : ''}{s.suit_type}
                </span>
              ))}
            </div>
          )}

          {/* Suggest buying */}
          {hasMissing && (
            <button
              data-testid="suit-add-suggestion"
              onClick={() => navigate('/garage')}
              className="mt-2 text-[10px] text-secondary hover:text-secondary/80 hover:underline transition-colors flex items-center gap-1"
            >
              <ShoppingBag className="w-2.5 h-2.5" />
              Add to your garage
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
